import { useState, useEffect } from 'react';

interface QuickAddSheetProps {
  open: boolean;
  onClose: () => void;
  onAdd: (data: { title: string; projectName: string; priority: string; dueDate: string }) => void;
  projects: { id: string; name: string }[];
}

const priorities = [
  { value: '高', label: '高优先级', color: 'bg-red-50 text-red-600 border-red-200' },
  { value: '中', label: '中优先级', color: 'bg-amber-50 text-amber-600 border-amber-200' },
  { value: '低', label: '低优先级', color: 'bg-secondary-100 text-secondary-600 border-secondary-200' },
];

export default function QuickAddSheet({ open, onClose, onAdd, projects }: QuickAddSheetProps) {
  const [title, setTitle] = useState('');
  const [projectName, setProjectName] = useState('');
  const [priority, setPriority] = useState('中');
  const [dueDate, setDueDate] = useState(new Date().toISOString().split('T')[0]);

  useEffect(() => {
    if (open) {
      setTitle('');
      setProjectName('');
      setPriority('中');
      setDueDate(new Date().toISOString().split('T')[0]);
    }
  }, [open]);

  const handleSubmit = () => {
    if (!title.trim()) return;
    onAdd({
      title: title.trim(),
      projectName,
      priority,
      dueDate,
    });
    onClose();
  };

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/20 z-40 transition-opacity"
        onClick={onClose}
      ></div>

      {/* Sheet */}
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-background-50 rounded-t-2xl animate-slide-up"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
        {/* Handle */}
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-8 h-1 rounded-full bg-background-300"></div>
        </div>

        <div className="px-5 pb-6">
          <h3 className="text-sm font-semibold text-foreground-800 mb-4 font-heading">快速新增待办</h3>

          {/* Title */}
          <input
            type="text"
            placeholder="输入任务名称..."
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full text-sm px-3 py-2.5 rounded-lg bg-background-100 border border-background-200/60 text-foreground-800 placeholder-foreground-400 outline-none focus:border-primary-300 transition-colors"
            autoFocus
          />

          {/* Project selector */}
          <select
            value={projectName}
            onChange={(e) => setProjectName(e.target.value)}
            className="w-full mt-2 text-sm px-3 py-2.5 rounded-lg bg-background-100 border border-background-200/60 text-foreground-800 outline-none focus:border-primary-300 transition-colors cursor-pointer"
          >
            <option value="">不关联项目</option>
            {projects.map((p) => (
              <option key={p.id} value={p.name}>{p.name}</option>
            ))}
          </select>

          {/* Priority */}
          <div className="flex items-center gap-2 mt-2">
            {priorities.map((p) => (
              <button
                key={p.value}
                onClick={() => setPriority(p.value)}
                className={`flex-1 text-xs px-2 py-2 rounded-lg border font-medium transition-all cursor-pointer whitespace-nowrap ${
                  priority === p.value
                    ? `${p.color} border-current`
                    : 'bg-background-100 border-background-200/60 text-foreground-500'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>

          {/* Date */}
          <input
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            className="w-full mt-2 text-sm px-3 py-2.5 rounded-lg bg-background-100 border border-background-200/60 text-foreground-800 outline-none focus:border-primary-300 transition-colors cursor-pointer"
          />

          {/* Actions */}
          <div className="flex items-center gap-2 mt-4">
            <button
              onClick={onClose}
              className="flex-1 text-sm py-2.5 rounded-lg text-foreground-500 bg-background-100 font-medium transition-colors hover:bg-background-200 cursor-pointer whitespace-nowrap"
            >
              取消
            </button>
            <button
              onClick={handleSubmit}
              disabled={!title.trim()}
              className={`flex-1 text-sm py-2.5 rounded-lg font-medium transition-colors cursor-pointer whitespace-nowrap ${
                title.trim()
                  ? 'bg-primary-500 text-white hover:bg-primary-600'
                  : 'bg-background-200 text-foreground-400 cursor-not-allowed'
              }`}
            >
              添加任务
            </button>
          </div>
        </div>
      </div>
    </>
  );
}